import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, AlertTitle, Box } from '@mui/material';
import { getWalletBalance } from '../../pages/WalletDetails/walletSlice';
import { decodeToken } from 'react-jwt';
import { formatCurrencyAmount } from '../../utils/taxUtils';

/**
 * LowBalanceAlert Component
 * Shows an alert when wallet balance is below the entity's configured
 * low-balance threshold (or the 25 GBP platform default), or negative.
 *
 * The threshold and the "is low / is negative" comparison are both computed
 * server-side (see wallet.getWalletBalance) in the same currency the balance
 * is displayed in, so a threshold set in one currency (e.g. GBP) is always
 * compared correctly against a balance held in another (e.g. AED) - this
 * component never compares raw numbers across currencies itself.
 */
const LowBalanceAlert = ({
  showOnNegativeOnly = false,
  persistent = false,
  onLoginCheck = false
}) => {
  const dispatch = useDispatch();
  const walletBalance = useSelector((state) => state.wallet?.walletBalance);
  const walletCurrencyCode = useSelector((state) => state.wallet?.currencyCode);
  const walletCurrencySymbol = useSelector((state) => state.wallet?.currencySymbol);
  const isBalanceLoaded = useSelector((state) => state.wallet?.isBalanceLoaded);
  const lowBalanceThreshold = useSelector((state) => state.wallet?.lowBalanceThreshold);
  const isLowBalance = useSelector((state) => state.wallet?.isLowBalance);
  const isNegativeBalance = useSelector((state) => state.wallet?.isNegativeBalance);
  const [showAlert, setShowAlert] = useState(false);
  const [wasShown, setWasShown] = useState(false);

  const userToken = localStorage.getItem('token');
  const decodedToken = decodeToken(userToken);
  const isSuperAdmin = decodedToken?.isSuperAdmin || false;
  const isEnterpriseAdmin = decodedToken?.isEnterpriseAdmin || false;
  const hasOomeroBillingAccess = Boolean(
    decodedToken?.isOomero && decodedToken?.roles?.permissions?.view_client_billing_menu
  );
  const isClientWalletUser = Boolean(
    !decodedToken?.isOomero ||
    decodedToken?.packageType === 'prepaid' ||
    decodedToken?.packageType === 'postpaid'
  );

  const shouldCheckBalance = Boolean(decodedToken) && !isSuperAdmin && (
    isEnterpriseAdmin ||
    isClientWalletUser ||
    hasOomeroBillingAccess
  );

  useEffect(() => {
    if (!shouldCheckBalance) return;
    dispatch(getWalletBalance());
  }, [dispatch, shouldCheckBalance]);

  useEffect(() => {
    if (!shouldCheckBalance || !isBalanceLoaded) {
      setShowAlert(false);
      return;
    }

    let shouldShow = showOnNegativeOnly ? isNegativeBalance : (isNegativeBalance || isLowBalance);

    if (onLoginCheck && wasShown) {
      shouldShow = false;
    }

    setShowAlert(shouldShow);

    if (shouldShow && onLoginCheck) {
      setWasShown(true);
    }

    if (shouldShow && !persistent) {
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [
    isLowBalance,
    isNegativeBalance,
    showOnNegativeOnly,
    persistent,
    onLoginCheck,
    wasShown,
    shouldCheckBalance,
    isBalanceLoaded,
  ]);

  if (!showAlert || !shouldCheckBalance || !isBalanceLoaded) return null;

  const formattedBalance = formatCurrencyAmount(
    walletBalance ?? 0,
    walletCurrencySymbol,
    walletCurrencyCode,
  );
  const numericThreshold = Number(lowBalanceThreshold);
  const formattedThreshold = Number.isFinite(numericThreshold)
    ? formatCurrencyAmount(numericThreshold, walletCurrencySymbol, walletCurrencyCode)
    : null;

  const getSeverity = () => {
    if (isNegativeBalance) return 'error';
    if (Number.isFinite(numericThreshold) && Number(walletBalance) < numericThreshold / 2) return 'error';
    return 'warning';
  };

  const getTitle = () => {
    if (isNegativeBalance) return 'Negative Wallet Balance';
    return 'Low Wallet Balance';
  };

  const getMessage = () => {
    const minimumClause = formattedThreshold
      ? ` The minimum required balance is ${formattedThreshold}.`
      : '';
    if (isNegativeBalance) {
      return `Your wallet balance is negative (${formattedBalance}).${minimumClause} Please top up immediately to continue using services.`;
    }
    return `Your wallet balance is low (${formattedBalance}).${minimumClause} Please top up to continue using services.`;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity={getSeverity()}
        onClose={persistent ? undefined : () => setShowAlert(false)}
        sx={{
          '& .MuiAlert-message': { width: '100%' },
          animation: 'slideDown 0.3s ease-out',
          '@keyframes slideDown': {
            from: { transform: 'translateY(-20px)', opacity: 0 },
            to: { transform: 'translateY(0)', opacity: 1 }
          }
        }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>{getTitle()}</AlertTitle>
        {getMessage()}
        <Box sx={{ mt: 1, fontWeight: 600, fontSize: '1.1rem' }}>
          Current Balance: {formattedBalance}
        </Box>
      </Alert>
    </Box>
  );
};

export default LowBalanceAlert;
